'use client';

import { BodyLinkV3, TypographyV3 } from '@jouzen/ecom-components';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { EventType } from '@/analytics/types';

import VectorImage from '../VectorImage';

const LOCATION = 'eyebrow banner';

// Only running in U.S. No translation needed
export const AffirmMessage = (): JSX.Element => (
  <div
    className="flex justify-center text-black"
    data-cy="affirm-eyebrow-message"
  >
    <VectorImage name="affirm" width={50} />
    <TypographyV3
      color="inherit"
      variant="body-small"
      weight="bold"
      className="ml-2 mr-6"
    >
      Pay over time
    </TypographyV3>
    <TypographyV3 color="inherit" variant="body-small">
      As low as 0% APR
    </TypographyV3>
  </div>
);

// Only running in Japan, no translation needed
export const JapanMessage = (): JSX.Element => {
  const path = usePathname();

  const handleAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      location: LOCATION,
      cta: 'Softbank',
      page: path,
      path: '/sizing#size-in-store',
      title:
        'Ouraリングは一部のソフトバンク店舗と家電量販店でお求めいただけます。 詳しくはこちらをご覧ください。',
      participatingCountries: ['JP'],
    });
  };

  return (
    <TypographyV3
      data-cy="japan-eyebrow-message"
      variant="body-small"
      weight="normal"
      color="light"
      align="center"
      className="px-2"
    >
      <span data-cy="japan-eyebrow-message-text">
        Ouraリングは一部のソフトバンク店舗と家電量販店でお求めいただけます。
      </span>
      <BodyLinkV3
        href="/sizing#size-in-store"
        color="inherit"
        onClick={handleAnalytics}
      >
        詳しくはこちらをご覧ください。
      </BodyLinkV3>
    </TypographyV3>
  );
};

// Only running in U.S. and Canada
export const BestBuyMessage = (): JSX.Element => {
  const path = usePathname();
  const t = useTranslations();

  const handleAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      location: LOCATION,
      cta: 'Best Buy',
      page: path,
      path: '/best-buy/store-locator',
      title: 'Want more buying options? Find us in a Best Buy near you.',
      participatingCountries: ['US', 'CA'],
    });
  };

  return (
    <div
      className="flex items-center justify-center md:flex-row"
      data-cy="best-buy-eyebrow-message"
    >
      <VectorImage name="best-buy" width={50} />
      <div className="flex flex-col md:flex-row">
        <TypographyV3
          variant="body-small"
          weight="bold"
          className="text-center"
        >
          {t.rich('best_buy_buying_options_eyebrow', {
            link: function link(chunks) {
              return (
                <Link
                  href="/best-buy/store-locator"
                  className="underline"
                  onClick={handleAnalytics}
                >
                  {chunks}
                </Link>
              );
            },
          })}
        </TypographyV3>
      </div>
    </div>
  );
};

export const NaturalCyclesMessage = (): JSX.Element => {
  const t = useTranslations();
  const path = usePathname();

  const handleAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      location: LOCATION,
      cta: 'Natural Cycles',
      page: path,
      path: '/getstarted',
      title: 'Natural Cycles Hormone-free Birth Control. Get started now.',
      participatingCountries: ['US'],
    });
  };

  return (
    <TypographyV3
      variant="body-small"
      weight="bold"
      className="text-center"
      data-cy="natural-cycles-eyebrow-message"
    >
      {t.rich('natural_cycles_eyebrow_message', {
        link: function link(chunks) {
          return (
            <Link
              href="/getstarted"
              className="underline"
              onClick={handleAnalytics}
            >
              {chunks}
            </Link>
          );
        },
      })}
    </TypographyV3>
  );
};

export const FSAMessage = (): JSX.Element => {
  const t = useTranslations();
  const path = usePathname();

  const handleAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      location: LOCATION,
      cta: 'FSA/HSA',
      page: path,
      path: '/product/rings',
      title: 'Shop now and use your HSA/FSA funds at check out.',
      participatingCountries: ['US'],
    });
  };

  return (
    <TypographyV3
      variant="body-small"
      weight="bold"
      className="text-center"
      data-cy="fsa-eyebrow-message"
    >
      {t.rich('fsa_eyebrow_message', {
        link: function link(chunks) {
          return (
            <Link
              href="/product/rings"
              className="underline"
              onClick={handleAnalytics}
            >
              {chunks}
            </Link>
          );
        },
      })}
    </TypographyV3>
  );
};

export const CorpGiftingMessage = (): JSX.Element => {
  const t = useTranslations();
  const path = usePathname();

  const handleAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      location: LOCATION,
      cta: 'Corporate Gifting',
      page: path,
      path: 'https://app.smartsheet.com/b/form/41a3a719a4784cfcbc24c0f6974ec04f',
      title: 'Bulk gift cards now available! Order here.',
      participatingCountries: ['US'],
    });
  };

  return (
    <TypographyV3
      variant="body-small"
      weight="bold"
      color="light"
      className="px-1 py-3.5 text-center"
      data-cy="corporate-gifts-eyebrow-message"
    >
      {t.rich('digital_corp_gift_card_eyebrow', {
        link: function link(chunks) {
          return (
            <BodyLinkV3
              external
              href="https://app.smartsheet.com/b/form/41a3a719a4784cfcbc24c0f6974ec04f"
              target="_blank"
              rel="noreferrer"
              color="inherit"
              onClick={handleAnalytics}
            >
              {chunks}
            </BodyLinkV3>
          );
        },
      })}
    </TypographyV3>
  );
};

export const ExpeditedShippingMessage = (): JSX.Element => {
  const t = useTranslations();
  return (
    <div
      className="flex justify-center text-white"
      data-cy="expedited-shipping-eyebrow-message"
    >
      <TypographyV3 color="inherit" variant="body-small" weight="bold">
        {t('expedited_shipping_banner')}
      </TypographyV3>
    </div>
  );
};

export const AffiliateMessage = (): JSX.Element => (
  <div id="partner_eyebrow_text" data-cy="partner-eyebrow-message" />
);

// Only running in U.S.
export const AmazonMessage = (): JSX.Element => {
  const t = useTranslations();
  const langSpecificIcon = t('amazon_eyebrow_translated_icon');
  const path = usePathname();

  const handleAnalytics = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      location: LOCATION,
      cta: 'Amazon Message',
      page: path,
      path: 'https://www.amazon.com/dp/B0CSR7G7NX',
      title: 'Now Available on Amazon - Shop Now',
      participatingCountries: ['US'],
    });
  };

  return (
    <div
      className="flex items-center justify-center md:flex-row"
      data-cy="amazon-eyebrow-message"
    >
      <VectorImage
        name={langSpecificIcon}
        width={140}
        height={20}
        className="mt-2.5"
        color="inherit"
      />
      <div className="-ml-1.5 flex flex-col md:flex-row">
        <TypographyV3
          variant="body-small"
          weight="bold"
          className="text-center"
        >
          {t.rich('amazon_now_available_eyebrow', {
            link: function link(chunks: ReactNode) {
              return (
                <Link
                  href="https://www.amazon.com/dp/B0CSR7G7NX?th=1"
                  className="underline"
                  onClick={handleAnalytics}
                >
                  {chunks}
                </Link>
              );
            },
          })}
        </TypographyV3>
      </div>
    </div>
  );
};

export interface GenericEyebrowProps {
  readonly localeKey: MessageKey;
  readonly url?: string;
  readonly additionalSegmentProperties?: Record<string, unknown>;
}
/**
 * GenericEyebrow component driven by config in Statsig
 * */
export const GenericEyebrow = ({
  localeKey,
  url = '',
  additionalSegmentProperties,
}: GenericEyebrowProps): JSX.Element => {
  const t = useTranslations();
  const page = usePathname();

  const onLinkClick = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.LinkClicked, {
      location: LOCATION,
      page,
      ...additionalSegmentProperties,
    });
  };

  return (
    <TypographyV3 variant="body-small" weight="bold" className="text-center">
      {t.rich(localeKey, {
        link: function link(chunks) {
          return (
            <Link href={url} className="underline" onClick={onLinkClick}>
              {chunks}
            </Link>
          );
        },
      })}
    </TypographyV3>
  );
};
